/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Component from 'can/component/component';
import template from './timeline.stache';
import Swiper from 'swiper';
import DomEventsManager from 'scripts/dom-events-manager';
import urlUtil from 'scripts/utils/url';
import can from 'can';
import appContext from 'scripts/app-context';
import FeedService from 'scripts/services/base-feed-service';
import _ from 'lodash';
import dtm from 'scripts/services/dtm';
import device from 'scripts/utils/device';

const resultsPageUrl = window.globalSettings.pagePaths.historyScoringPage;
const statsPageUrl = window.globalSettings.pagePaths.historyStatisticsPage;
const historyTimelineFeedUrl = window.globalSettings.feeds.history.timeline;
const subscriptionKey = window.globalSettings.feeds.history.subscriptionKey;

const cardsPerPage = 15;
const cardsRenderThreshold = 5;

const findFirstContentCard = (yearCards) => {
    const contentCardCondition = (slide) => !slide.isDivider;
    const firstYearCard = yearCards.find((yearObject) =>
        yearObject.slides.some(contentCardCondition),
    );
    const firstSlide = firstYearCard.slides.find(contentCardCondition);
    return firstSlide.cards[0];
};

Component.extend({
    tag: 'history-experience-timeline',
    template: template,
    leakScope: false,
    viewModel: {
        define: {
            timelineData: {
                Type: can.Map,
                Value: can.Map,
            },
            currentYear: {
                type: 'number',
                value: null,
            },
            activeSlide: {
                type: 'string',
                value: '0',
            },
            isModalShown: {
                type: 'boolean',
                value: false,
            },
            activeCardIndex: {
                type: 'number',
                value: 0,
            },
            activeCardPage: {
                get() {
                    return Math.floor(
                        this.attr('activeCardIndex') / cardsPerPage,
                    );
                },
            },
            isMobile: {
                get() {
                    return (
                        ['small', 'extra-small'].indexOf(
                            appContext.model.screen.attr('screenSize'),
                        ) > -1
                    );
                },
            },
            isMobileOrTablet: {
                get() {
                    return (
                        ['large', 'medium', 'small', 'extra-small'].indexOf(
                            appContext.model.screen.attr('screenSize'),
                        ) > -1 && device.isTouch
                    );
                },
            },
            slides: {
                get() {
                    return Object.values(this.attr('timelineData.years') || {})
                        .reduce((list, data, yearIndex) => {
                            const slides = data.slides || [];

                            if (
                                data.divider &&
                                data.divider.displayType !== 'none'
                            ) {
                                slides.unshift(data.divider);
                                slides[0].attr('isDivider', true);
                            }

                            return Array.prototype.concat.apply(
                                list,
                                slides.map((slide, slideIndexInYear) => {
                                    slide.attr('year', data.year);
                                    slide.attr('yearIndex', yearIndex);
                                    slide.attr(
                                        'slideIndexInYear',
                                        slideIndexInYear,
                                    );
                                    slide.attr(
                                        'resultsPageUrl',
                                        resultsPageUrl,
                                    );
                                    slide.attr('statsPageUrl', statsPageUrl);

                                    return slide;
                                }),
                            );
                        }, [])
                        .map((slide, slideIndex) => {
                            slide.attr('index', slideIndex);
                            slide.attr(
                                'hash',
                                slide.attr('cards.0.id') ||
                                    _.uniqueId('slide-'),
                            );

                            return slide;
                        });
                },
            },
        },
    },
    events: {
        inserted() {
            this.registerRoutes();
            DomEventsManager.subscribe(this);
            this.registerListeners();
            this.registerFeedService();
            this.feedService.fetch();
        },

        registerRoutes() {
            can.route('m=:cardIdentifier');
            can.route(':identifier');
            can.route.ready();
        },

        doRoute(routeAttr) {
            can.route.attr(routeAttr, true);
        },

        getCurrentHashObject() {
            if (can.route.attr('m')) {
                return {
                    value: can.route.attr('m'),
                    isCard: true,
                };
            } else if (can.route.attr('identifier')) {
                const hashKeyValue = can.route.attr('identifier').split('=');
                const isCardOpened = hashKeyValue.length > 1;
                return {
                    value: isCardOpened ? hashKeyValue[1] : hashKeyValue[0],
                    isCard: isCardOpened,
                };
            }
            return {
                value: urlUtil.getHash().slice(1),
                isCard: false,
            };
        },

        registerListeners() {
            document.addEventListener(
                'touchmove',
                (event) => {
                    const selectionListItemClass =
                        's-history-experience-timeline-historybar-year-selection-list__item';
                    if (
                        event.target.classList &&
                        !event.target.classList.contains(
                            selectionListItemClass,
                        ) &&
                        !event.target.classList.contains('s-card__img')
                    ) {
                        event.preventDefault();
                    }
                },
                { passive: false },
            );

            window.addEventListener('orientationchange', () => {
                this.reinitSliders(true);
            });
        },

        registerFeedService() {
            const headers = subscriptionKey
                ? { ['Ocp-Apim-Subscription-Key']: subscriptionKey }
                : {};

            this.feedService = new FeedService({
                feedUrl: historyTimelineFeedUrl,
                fromCache: true,
                autoUpdate: false,
                headers,
            })
                .on('fetching', (ev, value) =>
                    this.viewModel.attr('fetching', value),
                )
                .on('data', (ev, data) => {
                    this.viewModel.attr('timelineData', data);

                    this.viewModel.attr(
                        'currentYear',
                        _.get(data, 'years.0.year'),
                    );

                    this.initSlider();

                    this.initModalEvents();

                    this.viewModel.attr('fetching', false);
                });
        },

        hideFocusHiddenSlides(swiperContainer) {
            swiperContainer.find('.swiper-slide').attr('tabindex', '-1');
            swiperContainer
                .find('.swiper-slide')
                .find('a')
                .attr('tabindex', '-1');
            swiperContainer
                .find('.swiper-slide')
                .find('button')
                .attr('tabindex', '-1');
            swiperContainer.find('.swiper-slide-visible').attr('tabindex', '0');
            swiperContainer
                .find('.swiper-slide-visible')
                .find('a')
                .attr('tabindex', '0');
            swiperContainer
                .find('.swiper-slide-visible')
                .find('button')
                .attr('tabindex', '0');
        },

        initSlider(initialSlide) {
            if (this.swiper) {
                this.swiper.destroy();
            }
            let swiperContainer = this.element.find(
                    '.s-history-experience-timeline-slider',
                ),
                slideNumber = this.element.find('.swiper-slide').length;

            if (slideNumber > 1) {
                this.swiper = new Swiper(swiperContainer, {
                    init: false,
                    initialSlide: initialSlide ? initialSlide : 0,
                    spaceBetween: 0,
                    direction: this.viewModel.attr('isMobile')
                        ? 'vertical'
                        : 'horizontal',
                    freeMode: true,
                    freeModeMomentumRatio: 0.6,
                    freeModeSticky: !this.viewModel.attr('isMobile'),
                    mousewheel: !this.viewModel.attr('isModalShown'),
                    keyboard: {
                        enabled: !this.viewModel.attr('isModalShown'),
                    },
                    slidesPerView: 'auto',
                    preloadImages: false,
                    watchSlidesProgress: true,
                    watchSlidesVisibility: true,
                    lazy: {
                        loadPrevNext: true,
                        loadPrevNextAmount: 8,
                    },
                    centeredSlides: false,
                    simulateTouch: true,
                    hashNavigation: {
                        replaceState: true,
                    },
                    navigation: {
                        prevEl: swiperContainer.find('.swiper-button--prev'),
                        nextEl: swiperContainer.find('.swiper-button--next'),
                    },
                });

                this.swiper.on('init', () => {
                    this.updateSliderData(this.swiper);
                    this.showSlidesCards(this.swiper);
                    this.hideFocusHiddenSlides(swiperContainer);
                });

                this.swiper.on('slideChangeTransitionStart', () => {
                    this.updateSliderData(this.swiper);
                    this.showSlidesCards(this.swiper);
                });

                this.swiper.on('slideChangeTransitionEnd', () => {
                    this.updateSliderData(this.swiper);
                    this.showSlidesCards(this.swiper);
                    this.hideFocusHiddenSlides(swiperContainer);
                });

                this.swiper.on('sliderMove', () => {
                    this.updateSliderData(this.swiper);
                    this.showSlidesCards(this.swiper);
                });

                this.swiper.on('setTranslate', () => {
                    this.updateSliderData(this.swiper);
                    setTimeout(() => this.showSlidesCards(this.swiper), 300);
                });

                this.swiper.init();

                if (this.viewModel.attr('isMobile')) {
                    const height =
                        can.$('body').height() -
                        can.$('.c-history-experience-header').height() -
                        can
                            .$('.s-history-experience-timeline-historybar')
                            .height() *
                            1.5;
                    this.swiper.params.height = height;
                    this.swiper.update();
                }

                if (this.viewModel.attr('isMobileOrTablet')) {
                    this.element
                        .find(
                            '.s-history-experience-timeline-slider .swiper-button',
                        )
                        .hide();
                }
            }
        },
        updateSliderData: _.throttle(function(swiper) {
            if (
                !this.viewModel.attr('isModalShown') &&
                swiper.slides[swiper.activeIndex]
            ) {
                this.viewModel.attr(
                    'currentYear',
                    swiper.slides[swiper.activeIndex].dataset.year,
                );
                this.viewModel.attr(
                    'activeCardIndex',
                    swiper.slides[swiper.activeIndex].dataset.cardIndex,
                );
            }
        }, 300),

        initModalEvents() {
            const modalDialogViewModel = this.element
                .find('timeline-modal-dialog')
                .viewModel();

            modalDialogViewModel.on('modalOpened', () => {
                this.viewModel.attr('isModalShown', true);

                this.swiper.params.hashNavigation = false;

                if (this.modalSwiper) {
                    this.modalSwiper.params.hashNavigation = {
                        replaceState: true,
                    };
                }
            });

            modalDialogViewModel.on('modalClosed', () => {
                this.viewModel.attr('isModalShown', false);
                const hashValue = this.getCurrentHashObject().value;
                this.doRoute({ identifier: hashValue });

                this.swiper.params.hashNavigation = {
                    replaceState: true,
                };
                this.modalSwiper.params.hashNavigation = false;
            });

            const hashObject = this.getCurrentHashObject();
            let slide = this.element.find(
                `.swiper-slide[data-identifier="${hashObject.value}"]`,
            );
            if (slide) {
                this.setActiveSlide(slide.data('cardIndex'));
                if (hashObject.isCard) {
                    this.showModalSlider(slide.data('cardIndex') + 1);
                }
            }
        },

        showModalSlider(initialSlide) {
            const modalDialog = this.element.find('timeline-modal-dialog'),
                modalDialogParent = this.element.find(
                    '.c-history-experience-timeline',
                ),
                htmlTag = this.element.closest('html');
            if (modalDialog && modalDialog.length) {
                modalDialog.viewModel().show(modalDialogParent);
                modalDialogParent.addClass('is-down');
                modalDialog.append(
                    modalDialogParent.find('timeline-progressbar'),
                );
                this.initModalSlider(initialSlide);
                htmlTag.addClass('over-hidden');
            }
        },

        destroyModalSlider() {
            if (this.modalSwiper) {
                this.modalSwiper.destroy(false, true);
                this.modalSwiper = null;
            }
        },

        initModalSlider(initialSlide) {
            let swiperContainer = can.$(
                '.s-history-experience-timeline-modal-slider',
            );

            this.setActiveSlide(initialSlide);

            if (!this.modalSwiper) {
                this.modalSwiper = new Swiper(swiperContainer, {
                    init: false,
                    initialSlide: ~~initialSlide,
                    direction: this.viewModel.attr('isMobile')
                        ? 'vertical'
                        : 'horizontal',
                    spaceBetween: 0,
                    mousewheel: true,
                    keyboard: {
                        enabled: true,
                    },
                    centeredSlides: true,
                    slidesPerView: 1,
                    simulateTouch: true,
                    hash: {
                        replaceState: true,
                    },
                    navigation: {
                        prevEl: swiperContainer.find('.swiper-button--prev'),
                        nextEl: swiperContainer.find('.swiper-button--next'),
                    },
                });

                this.modalSwiper.on('init', () => {
                    this.viewModel.attr(
                        'currentYear',
                        this.modalSwiper.slides[this.modalSwiper.activeIndex]
                            .dataset.year,
                    );
                    this.setActiveSlide(
                        this.modalSwiper.slides[this.modalSwiper.activeIndex]
                            .dataset.cardIndex,
                    );
                    if (initialSlide === 0) {
                        const yearCards = this.viewModel
                            .attr('timelineData.years')
                            .serialize();
                        const firstCard = findFirstContentCard(yearCards);
                        const tag = firstCard.collectionTags
                            ? firstCard.collectionTags[0].toLowerCase()
                            : '';
                        dtm.trackHistoryContentView(
                            firstCard.title,
                            firstCard.cardType,
                            firstCard.cardYear,
                            tag,
                        );
                    }
                });

                this.modalSwiper.on('slideChangeTransitionStart', () => {
                    if (!this.viewModel.attr('isModalShown')) {
                        return;
                    }
                    this.viewModel.attr(
                        'currentYear',
                        this.modalSwiper.slides[this.modalSwiper.activeIndex]
                            .dataset.year,
                    );
                    this.setActiveSlide(
                        this.modalSwiper.slides[this.modalSwiper.activeIndex]
                            .dataset.cardIndex,
                    );
                    this.viewModel.dispatch('modalActiveChanged', [
                        this.modalSwiper.activeIndex,
                    ]);
                });

                this.modalSwiper.on('slideChangeTransitionEnd', () => {
                    if (!this.viewModel.attr('isModalShown')) {
                        return;
                    }

                    const slide = this.modalSwiper.slides[
                        this.modalSwiper.activeIndex
                    ];
                    const yearIndex = slide.dataset.yearIndex;
                    const slideIndexInYear = slide.dataset.slideIndexInYear;

                    const card = this.viewModel.attr(
                        `timelineData.years.${yearIndex}.slides.${slideIndexInYear}.cards.0`,
                    );
                    const tag = card.collectionTags
                        ? card.collectionTags[0].toLowerCase()
                        : '';

                    dtm.trackHistoryContentView(
                        card.title,
                        card.cardType,
                        card.cardYear,
                        tag,
                    );
                });

                this.modalSwiper.on('sliderMove', () => {
                    if (!this.viewModel.attr('isModalShown')) {
                        return;
                    }

                    this.viewModel.attr(
                        'currentYear',
                        this.modalSwiper.slides[this.modalSwiper.activeIndex]
                            .dataset.year,
                    );
                });

                this.modalSwiper.init();
            } else if (this.modalSwiper) {
                this.modalSwiper.slideTo(initialSlide, 0);
            }

            this.viewModel.dispatch('modalActiveChanged', [~~initialSlide]);
            const cardIdentifier = this.modalSwiper.slides[
                this.modalSwiper.activeIndex
            ].dataset.identifier;
            this.doRoute({ m: cardIdentifier });
        },

        setActiveSlide(cardIndex) {
            let activeSlide = cardIndex;
            let slide = this.swiper.$el.find(
                `button[data-card-index="${cardIndex}"]`,
            );
            if (!slide.length) {
                activeSlide = cardIndex - 1;
            }

            this.viewModel.attr('activeCardIndex', cardIndex);
            this.viewModel.attr('activeSlide', activeSlide);
        },

        reinitSliders(isOrientationChange) {
            let video = this.element.find('modal-card brightcove-video');
            if (video.length == 1) {
                let player = video.viewModel().player;
                if (
                    (!isOrientationChange && player.userActive()) ||
                    player.isFullscreen()
                ) {
                    return; //do not reinit sliders in case that resize event fired by interactions with video like fullscreen
                }
            }
            let activeCardIndex = this.viewModel.attr('activeCardIndex');
            this.swiper.destroy(false, true);
            this.initSlider(activeCardIndex);

            this.destroyModalSlider();
            if (this.viewModel.attr('isModalShown')) {
                this.initModalSlider(~~activeCardIndex + 1);
            }
        },

        showSlidesCards: function(swiper) {
            if (swiper) {
                const slideIndex = swiper.activeIndex;
                const slidesToShow = []
                    .concat(
                        Array.prototype.slice.call(
                            swiper.slides,
                            Math.max(0, slideIndex - cardsRenderThreshold),
                            slideIndex,
                        ),
                    )
                    .concat([swiper.slides[slideIndex]])
                    .concat(
                        Array.prototype.slice.call(
                            swiper.slides,
                            slideIndex + 1,
                            slideIndex + cardsRenderThreshold + 1,
                        ),
                    );
                const scrolled = Math.abs(Number(swiper.translate));
                const visible = scrolled + swiper.width;

                slidesToShow.forEach((slide) => {
                    const left = slide.swiperSlideOffset;
                    const right = left + can.$(slide).width();

                    if (
                        (left <= scrolled && right > scrolled) ||
                        (left >= scrolled && left < visible)
                    ) {
                        const slideIndex = Number(slide.dataset['slideIndex']);
                        const slides = this.viewModel.attr('slides');

                        if (!slides[slideIndex].isVisible) {
                            slides[slideIndex].attr('isVisible', true);
                        }
                    }
                });
            }
        },

        onWindowResize: function() {
            this.debouncedWindowResize();
        },

        debouncedWindowResize: _.debounce(function() {
            this.reinitSliders();
        }, 500),

        '.js-timeline-card click'(element) {
            const viewModel = element.find('timeline-card').viewModel();
            const videoName = viewModel.attr('videoName');
            const externalLink = viewModel.attr('externalLink');

            if (externalLink && externalLink.url) {
                if (externalLink.target === 'new') {
                    window.open(externalLink.url);
                } else {
                    location.assign(externalLink.url);
                }
                return;
            }

            this.showModalSlider(viewModel.attr('cardIndex'));
            if (videoName && !viewModel.attr('isMobile')) {
                dtm.trackHistoryVideoLauch(
                    videoName,
                    'History Timeline Content',
                );
            }
        },

        '{scope} currentYear'(scope, ev, currentYear) {
            if (this.swiper) {
                let slide = this.swiper.$el.find(
                    `button[data-year="${currentYear}"]`,
                );
                if (
                    this.swiper.slides[this.swiper.activeIndex].dataset.year !=
                    currentYear
                ) {
                    this.swiper.slideTo(slide.index(), 300);
                }

                if (
                    this.viewModel.attr('isModalShown') &&
                    this.modalSwiper &&
                    this.modalSwiper.slides[this.modalSwiper.activeIndex]
                        .dataset.year != currentYear
                ) {
                    let slide = this.modalSwiper.container.find(
                        `button[data-year="${currentYear}"]`,
                    );
                    this.modalSwiper.slideTo(slide.index(), 300);
                }
            }
        },
        '{scope} activeCardIndex'(scope, ev, activeCardIndex) {
            if (this.swiper) {
                let slide = this.swiper.$el.find(
                    `button[data-card-index="${activeCardIndex}"]`,
                );
                if (!slide.length) {
                    slide = this.swiper.$el.find(
                        `button[data-card-index="${activeCardIndex - 1}"]`,
                    );
                }
                if (
                    this.swiper.slides[this.swiper.activeIndex].dataset
                        .cardIndex != activeCardIndex
                ) {
                    this.swiper.slideTo(slide.index(), 300);
                }
            }
        },
    },
});
